import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import {
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonLabel,
    IonList,
} from "@ionic/react";
import Comprehension from "../../ClassThree/English/Comprehension";

// Question Type Definitions
interface BaseQuestion {
    question: string;
}

interface MultipleChoiceQuestion extends BaseQuestion {
    type: "multiple-choice";
    options: string[];
    answer: string;
}

interface FillInTheBlankQuestion extends BaseQuestion {
    type: "fill-in-the-blank";
    options: string[];
    answer: string;
}

interface TrueFalseQuestion extends BaseQuestion {
    type: "true-false";
    options?: string[];
    answer: string;
}

// Union type for all question types
type Question =
    | MultipleChoiceQuestion
    | FillInTheBlankQuestion
    | TrueFalseQuestion


const getRandomInt = (min: number, max: number): number =>
    Math.floor(Math.random() * (max - min + 1)) + min;

const IEO: React.FC = () => {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [matchOption, setMatchOption] = useState<string[] | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedType, setSelectedType] = useState(0);

    const loadRandomSyllabusQuestions = async () => {

        try {
            const data: Question[] = [];
            if (process.env.REACT_APP_DEVELOPMENT !== "WEB") {
                let response = await fetch(process.env.REACT_APP_API_URL + "ClassThreeEnglish/Nouns.json");
                data.push(...await response.json());
                response = await fetch(process.env.REACT_APP_API_URL + "ClassThreeEnglish/Pronouns.json");
                data.push(...await response.json());
                response = await fetch(process.env.REACT_APP_API_URL + "ClassThreeEnglish/Verbs.json");
                data.push(...await response.json());
                response = await fetch(process.env.REACT_APP_API_URL + "ClassThreeEnglish/Adjectives.json");
                data.push(...await response.json());
                response = await fetch(process.env.REACT_APP_API_URL + "ClassThreeEnglish/Adverbs.json");
                data.push(...await response.json());
                response = await fetch(process.env.REACT_APP_API_URL + "ClassThreeEnglish/Prepositions.json");
                data.push(...await response.json());
                response = await fetch(process.env.REACT_APP_API_URL + "ClassThreeEnglish/Conjunctions.json");
                data.push(...await response.json());
                response = await fetch(process.env.REACT_APP_API_URL + "ClassThreeEnglish/Interjections.json");
                data.push(...await response.json());
                response = await fetch(process.env.REACT_APP_API_URL + "ClassThreeEnglish/Sentences.json");
                data.push(...await response.json());
                response = await fetch(process.env.REACT_APP_API_URL + "ClassThreeEnglish/Tenses.json");
                data.push(...await response.json());
                response = await fetch(process.env.REACT_APP_API_URL + "ClassThreeEnglish/Vocabulary.json");
                data.push(...await response.json());
                response = await fetch(process.env.REACT_APP_API_URL + "ClassThreeEnglish/Punctuation.json");
                data.push(...await response.json());
            } else {
                let module = await import(`../../../datas/ClassThreeEnglish/Nouns.json`);
                data.push(...module.default as Question[]);
                module = await import(`../../../datas/ClassThreeEnglish/Pronouns.json`);
                data.push(...module.default as Question[]);
                module = await import(`../../../datas/ClassThreeEnglish/Verbs.json`);
                data.push(...module.default as Question[]);
                module = await import(`../../../datas/ClassThreeEnglish/Adjectives.json`);
                data.push(...module.default as Question[]);
                module = await import(`../../../datas/ClassThreeEnglish/Adverbs.json`);
                data.push(...module.default as Question[]);
                module = await import(`../../../datas/ClassThreeEnglish/Prepositions.json`);
                data.push(...module.default as Question[]);
                module = await import(`../../../datas/ClassThreeEnglish/Conjunctions.json`);
                data.push(...module.default as Question[]);
                module = await import(`../../../datas/ClassThreeEnglish/Interjections.json`);
                data.push(...module.default as Question[]);
                module = await import(`../../../datas/ClassThreeEnglish/Sentences.json`);
                data.push(...module.default as Question[]);
                module = await import(`../../../datas/ClassThreeEnglish/Tenses.json`);
                data.push(...module.default as Question[]);
                module = await import(`../../../datas/ClassThreeEnglish/Vocabulary.json`);
                data.push(...module.default as Question[]);
                module = await import(`../../../datas/ClassThreeEnglish/Punctuation.json`);
                data.push(...module.default as Question[]);
            }
            setQuestions(shuffleArray(data));
            setCurrentQuestion(data[currentIndex]);
        } catch (err) {
            console.error("Failed to load questions:", err);
        }
    };

    const handleAnswerSubmit = (option: string) => { 
        setSelectedOption(option);
        if (!currentQuestion) return;
        if (option.trim().toLowerCase() === questions[currentIndex].answer.trim().toLowerCase()) {
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                    setShowImage(false);
                    setSelectedOption(null);
                    handlePatternComplete()
                }
                else {
                    setCurrentIndex(0);
                    loadRandomSyllabusQuestions();
                }
            }, 5000);
        }
    };

    // Function to shuffle an array
    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    useEffect(() => {
        loadRandomSyllabusQuestions();
        document.title =
            "Mamas Guidance - Olympiad - Class Three - International English Olympiad (IEO)";
    }, []);

    useEffect(() => {
        const patternType = getRandomInt(0, 2);
        setSelectedType(patternType);
    }, []);

    const handlePatternComplete = () => {
        const nextType = getRandomInt(0, 2); console.log('next type -', nextType)
        setSelectedType(nextType);
    };


    return (
        <>
            <Helmet>
                <meta name="description" content="Prepare for Class 3 IMO Olympiad with Mama's Guidance. Access syllabus details, sample questions, tips, and practice worksheets to boost your child's performance." />
            </Helmet>
            {!showImage ? (
                <div className="contentPart showCenter additionWord">
                    {(selectedType === 0 || selectedType === 1)? (
                        <>
                            {questions[currentIndex] ? (
                                <>
                                    <h3 className="mb-3">{questions[currentIndex].question}</h3>
                                    {(questions[currentIndex].type === "multiple-choice" ||
                                        questions[currentIndex].type === "fill-in-the-blank") && (
                                            <IonRadioGroup
                                                onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                            >
                                                <IonList>
                                                    {(questions[currentIndex]?.options || []).map((option, index) => (
                                                        <IonItem
                                                            className={`grid-item ${selectedOption === option
                                                                ? option.toLowerCase() === questions[currentIndex].answer.toLowerCase()
                                                                    ? "correct"
                                                                    : "errorItems"
                                                                : ""
                                                                }`}
                                                            key={index}
                                                        >
                                                            <IonRadio slot="start" value={option} />
                                                            <IonLabel>{option}</IonLabel>
                                                        </IonItem>
                                                    ))}
                                                </IonList>
                                            </IonRadioGroup>
                                        )}
                                    {questions[currentIndex].type === "true-false" && (
                                        <IonRadioGroup
                                            onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                        >
                                            <IonList>
                                                {["true", "false"].map((option, index) => (
                                                    <IonItem key={index} className={`${selectedOption === option
                                                        ? option.toLowerCase() === questions[currentIndex].answer
                                                            ? "correct"
                                                            : "errorItems"
                                                        : ""
                                                        }`}>
                                                        <IonRadio slot="start" value={option} />
                                                        <IonLabel>{option.toUpperCase()}</IonLabel>
                                                    </IonItem>
                                                ))}
                                            </IonList>
                                        </IonRadioGroup>
                                    )}
                                </>
                            ) : (
                                <p>Loading question...</p>
                            )}
                        </>) : (
                        <>
                            <Comprehension onComplete={handlePatternComplete} />
                        </>

                    )}
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img
                        style={{ height: "150px" }}
                        className="img-fluid"
                        src={process.env.REACT_APP_WELDONE_LINK}
                        alt="Well done"
                    />
                </div>
            )}
        </>
    );
};

export default IEO;
